import { useCallback, useState } from 'react';
import { Alert } from '@piggytechng/component-library';
import { httpClient } from '../api/http';
import { ManagedAccountRiskView } from '../types';

/**
 * Places and clears ops holds on a managed account.
 *   POST /v5/admin/managed-accounts/:ref/hold        body { reason }
 *   POST /v5/admin/managed-accounts/:ref/hold/clear  body { note }
 * httpClient baseURL already includes /v5, so the paths omit it.
 */
export const useManagedAccountHolds = () => {
  const [isRunningHoldAction, setIsRunningHoldAction] = useState(false);
  const [riskView, setRiskView] = useState<ManagedAccountRiskView | null>(null);

  const handleHoldError = (error: any) => {
    const code = error?.response?.data?.data?.code;
    if (code === 'MFA_REQUIRED' || code === 'MFA_STALE') {
      Alert.error('Re-verify MFA to continue, then try again.');
      return;
    }
    console.log(error?.response?.data?.message || 'An error occured trying to perform the action');
  };

  const placeHold = useCallback(async (ref: string, reason: string, callback?: () => void) => {
    try {
      setIsRunningHoldAction(true);
      const response = await httpClient.post<any>(`admin/managed-accounts/${ref}/hold`, { reason });
      if (response.data.status) {
        setRiskView(response.data.data ?? null);
        Alert.success(response.data.message || 'Hold placed');
        callback?.();
      }
    } catch (error: any) {
      handleHoldError(error);
    } finally {
      setIsRunningHoldAction(false);
    }
  }, []);

  const clearHold = useCallback(async (ref: string, note: string, callback?: () => void) => {
    try {
      setIsRunningHoldAction(true);
      const response = await httpClient.post<any>(`admin/managed-accounts/${ref}/hold/clear`, { note });
      if (response.data.status) {
        setRiskView(response.data.data ?? null);
        Alert.success(response.data.message || 'Hold cleared');
        callback?.();
      }
    } catch (error: any) {
      handleHoldError(error);
    } finally {
      setIsRunningHoldAction(false);
    }
  }, []);

  return {
    placeHold,
    clearHold,
    isRunningHoldAction,
    riskView,
  };
};

export default useManagedAccountHolds;
